/**
 * This module handles numeric data visualization with multi-hue gradients (jet-style: blue → green → yellow → red).
 * Unlike continuous.ts which uses monochromatic OKLCH gradients, this creates more visually distinct colour ranges
 *  - useful when you want to show "cold to hot" data patterns.
 */

import * as d3 from 'd3';

import { createNumericScale } from './numeric-scale';
import type { ColourScale, ColourValue } from './types';

/** Default number of legend steps for heatmap scales */
const DEFAULT_LEGEND_STEPS = 5;

/** Default opacity when converting heatmap colours to ColourValue */
const DEFAULT_OPACITY = 0.8;

/** Options for creating a heatmap colour scale */
type HeatmapScaleOptions = {
  /** Number of legend steps (defaults to 5) */
  steps?: number;
  /** Custom label formatter (defaults to 3 decimal places) */
  formatLabel?: (value: number) => string;
};

/**
 * Interpolates a numeric value to a jet-style heatmap colour (blue → cyan → green → yellow → red).
 * Provides high visual contrast across the value range.
 *
 * @param value - The numeric value to convert to a colour
 * @param min - Minimum value of the range
 * @param max - Maximum value of the range
 * @returns CSS colour string in RGB format
 *
 * @example
 * ```ts
 * const colour = interpolateHeatmap(15, 10, 20); // Interpolates value 15 in range [10, 20] to a colour
 * console.log(colour); // e.g., 'rgb(255, 128, 0)' for a mid-range value
 * ```
 */
function interpolateHeatmap(value: number, min: number, max: number): string {
  if (max === min) return 'rgb(128, 128, 128)'; // Neutral grey for single value

  const t = (value - min) / (max - min);

  // Jet-style heatmap: blue → cyan → green → yellow → red
  // The jet colour map divides [0,1] into 4 regions:
  // t=0.00-0.25: blue→cyan  (b=1, g↑, r=0)
  // t=0.25-0.50: cyan→green (b↓, g=1, r=0)
  // t=0.50-0.75: green→yellow (b=0, g=1, r↑)
  // t=0.75-1.00: yellow→red (b=0, g↓, r=1)
  const r = Math.round(255 * Math.min(1, Math.max(0, 1.5 - Math.abs(4 * t - 3))));
  const g = Math.round(255 * Math.min(1, Math.max(0, 1.5 - Math.abs(4 * t - 2))));
  const b = Math.round(255 * Math.min(1, Math.max(0, 1.5 - Math.abs(4 * t - 1))));

  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Creates a ColourValue from an RGB colour string.
 * Generates a darker stroke colour automatically.
 *
 * @param fill - CSS colour string (RGB format)
 * @param opacity - Opacity value (0-1)
 * @returns ColourValue with fill, stroke, and opacity
 *
 * @example
 * ```ts
 * const colourValue = createHeatmapColourValue('rgb(255, 128, 0)', 0.8);
 * console.log(colourValue);
 * // {
 * //   fill: 'rgb(255, 128, 0)',
 * //   stroke: 'rgb(200, 100, 0)', // Automatically generated darker stroke
 * //   opacity: 0.8
 * // }
 * ```
 */
function createHeatmapColourValue(fill: string, opacity: number = DEFAULT_OPACITY): ColourValue {
  const stroke = d3.color(fill)?.darker(1).formatHex() ?? '#000000';
  return { fill, stroke, opacity };
}

/**
 * Creates a heatmap colour scale for numeric properties.
 * Uses a jet-style gradient (blue → green → yellow → red) for high visual contrast.
 *
 * @template T - The type of data items
 * @param data - Array of data items
 * @param property - Numeric property to base the scale on
 * @param options - Scale configuration options
 * @returns A continuous colour scale with heatmap interpolation
 *
 * @example
 * ```ts
 * const scale = createHeatmapScale(blocks, 'density', { steps: 5 });
 *
 * // Get colour for a value
 * const colour = scale.getColour(2.45);
 *
 * // Access legend for UI
 * scale.legend.forEach(item => console.log(item.label, item.colour));
 * ```
 */
function createHeatmapScale<T extends Record<string, unknown>>(
  data: T[],
  property: keyof T & string,
  options: HeatmapScaleOptions = {},
): Extract<ColourScale, { type: 'continuous' }> {
  const { steps = DEFAULT_LEGEND_STEPS, formatLabel = (v: number) => v.toFixed(3) } = options;

  return createNumericScale(data, property, {
    steps,
    formatLabel,
    interpolate: interpolateHeatmap,
  });
}

export {
  createHeatmapColourValue,
  createHeatmapScale,
  DEFAULT_LEGEND_STEPS as HEATMAP_DEFAULT_LEGEND_STEPS,
  type HeatmapScaleOptions,
  interpolateHeatmap,
};
